import { ComponentType } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, inject } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';

export type TableCellComponentData<T> = T | ((item: unknown) => T | Promise<T>);

@Component({ changeDetection: ChangeDetectionStrategy.Eager, template: '', standalone: false })
export abstract class TableCellComponent<T> {
  @HostBinding('class') protected _classes = 'flex';

  _item?: unknown;
  _data?: TableCellComponentData<T>;
  _config?: TableColumnConfig;

  data: T | null = null;

  private cdr = inject(ChangeDetectorRef);

  @PropertyListener('_config')
  private async _computeClasses() {
    const alignment =
      this._config?.align === 'center' ? 'justify-center' : this._config?.align === 'right' ? 'justify-end' : '';
    this._classes = `flex ${alignment}`;
    this.detectChangesSafe();
  }

  @PropertyListener('_data', '_item')
  private async _computeData() {
    if (!this._data || !this._item) {
      this.data = null;
    } else {
      this.data =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof this._data === 'function' ? await Promise.resolve((this._data as any)(this._item)) : this._data;
    }
    this.detectChangesSafe();
  }

  /** Runs CD immediately so async cell updates paint (markForCheck alone may never flush). */
  private detectChangesSafe() {
    try {
      this.cdr.detectChanges();
    } catch {
      // View may already be destroyed if the row was recycled while data was resolving.
    }
  }
}

export interface TableColumnConfig {
  header?: string;

  sticky?: boolean;
  stickyEnd?: boolean;

  fitContent?: boolean;

  allowUnsorted?: boolean;
  sortable?: boolean;
  sort?: 'ASC' | 'DESC';
  sortChanged?(sort?: 'ASC' | 'DESC'): void;

  hidden?: boolean;

  align?: 'left' | 'center' | 'right';
  customCellClasses?: string;
  customHeaderClasses?: string;
}

export abstract class TableColumn<I, T = unknown> {
  readonly id = '' + Math.random();

  abstract component: ComponentType<TableCellComponent<T>>;

  constructor(
    public config: TableColumnConfig,
    public data: T | ((item: I) => T | Promise<T>)
  ) {}

  toggleSort() {
    const sort = !this.config.sort ? 'ASC' : this.config.sort === 'ASC' ? 'DESC' : undefined;

    this.config.sort = this.config.allowUnsorted === false && !sort ? 'ASC' : sort;
    this.config.sortChanged?.(this.config.sort);
  }

  clearSort() {
    if (this.config.sort && this.config.allowUnsorted !== false) {
      this.config.sort = undefined;
      this.config.sortChanged?.(this.config.sort);
    }
  }
}
